import React, { useCallback, useState } from 'react';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { LoadSaveService } from 'gw-capability-quoteandbind';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { MLModalService } from 'ml-portals-services-react';
import { platformMessages } from 'ml-components-platform-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import { MLMultiLobsDataHolderService } from 'ml-services-common-react';
import styles from './AccountSyncPage.module.scss';
import './AccountSyncPage.messages';
import metadata from './AccountSyncPage.metadata.json5';
import { ScrollToError } from '@jutro/legacy/wizard-next';

const PERSONAL_AUTO = 'PersonalAuto';
const HOP_HOMEOWNERS = 'HOPHomeowners';
const PERSONAL_UMBRELLA = 'PersonalUmbrella';

const stepNames = {
    [PERSONAL_AUTO]: ['paGroupAssociations'],
    [HOP_HOMEOWNERS]: ['hoPrequalQuestions'],
    [PERSONAL_UMBRELLA]: ['pupPrequalQuestions']
};

const AccountSyncPage = (props) => {
    const {
        wizardData: submissionVM,
        updateWizardData,
        currentStep,
        steps,
        changeNextSteps
    } = props;
    const translator = useTranslator();
    const productCode = _.get(submissionVM, 'baseData.productCode.value');
    const [errorTimestamp, setErrorTimestamp] = useState(0);

    const {
        showProgressDialog,
        hideProgressDialog
    } = MLModalService();

    const {
        onValidate
    } = useValidation('AccountSyncPage');

    const onNext = useCallback(async () => {
        // jump to Quote Page for QnB HVQ
        if (submissionVM.hvqQuoteID_ML.value) {
            showProgressDialog(platformMessages.saveSubmissionAndSyncAccount);
            const responseSubmission = await LoadSaveService.saveSubmissionAndSyncAccount(submissionVM.value);
            submissionVM.value = responseSubmission;
            hideProgressDialog();

            const skipQuoteAndProceedToHVQDiscrepanciesCheck = submissionVM.value.isConsumerCompRater()
                    && !!MLMultiLobsDataHolderService.getFirstHVQConsumerNonCompletedStep()
                    && MLMultiLobsDataHolderService.hasCompletedStep('paHVQConsumerPrefillAndCarfax');

            if (!skipQuoteAndProceedToHVQDiscrepanciesCheck) {
                showProgressDialog(platformMessages.saveAndQuoteSubmission);
                const response = await LoadSaveService.saveAndQuoteSubmission(responseSubmission);
                submissionVM.value = response;
                MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, 'quoteDetails');
                hideProgressDialog();
            }

            const index = _.findIndex(
                steps,
                ({ path }) => path === '/quote'
            );
            if (index > 0) {
                const remainingSteps = steps.slice(index, steps.length);

                // filter out PAAdditionalInformationPreBindPage from wizard progress bar, added PAHVQAdditionalInformationPreBindPage
                const filteredRemainingSteps = remainingSteps.filter((item) => {
                    return item.path !== '/additional-information-prebind';
                });
                changeNextSteps(filteredRemainingSteps);
            }
        } else {
            const [nextStepName] = stepNames[productCode];
            if (!submissionVM.quoteID.value) {
                showProgressDialog(platformMessages.createSubmission);
                const submissionData = await LoadSaveService.createSubmission(submissionVM.value);
                _.set(submissionVM, 'value', submissionData);
            } else {
                showProgressDialog(platformMessages.saveSubmission);
                const submissionData = await LoadSaveService.updateDraftSubmission(submissionVM.value);
                _.set(submissionVM, 'value', submissionData);
            }
            hideProgressDialog();

            _.unset(submissionVM.value, 'bindData');
            if (!submissionVM.aspects.valid || !submissionVM.aspects.subtreeValid) {
                setErrorTimestamp(errorTimestamp+1);
                return false;
            }
            MLMultiLobsDataHolderService.storeFlowSteps(currentStep.mlStepName, nextStepName);
        }

        return submissionVM;
    }, [changeNextSteps, currentStep.mlStepName, errorTimestamp, hideProgressDialog, productCode, showProgressDialog, steps, submissionVM]);

    const writeNumericValue = (value) => {
        if (new RegExp('^\\d+$').test(value)) {
            _.set(submissionVM, 'baseData.accountHolder.lastFourDigitsSSN_ML.value', value);
            updateWizardData(submissionVM);
        }
    };

    const overrides = {
        ssn: {
            onValueChange: writeNumericValue
        }
    };

    const skipOnResumeIf = () => {
        if (!!_.get(submissionVM, 'bindData.policyNumber.value')
            && (submissionVM.value.baseData.status_ML === 'Bound' || submissionVM.value.baseData.status_ML === 'Binding')) {
            return true;
        }

        if (MLMultiLobsDataHolderService.getLastVisitedStep() === currentStep.mlStepName) {
            return false;
        }

        if (submissionVM.value.isConsumerCompRater()) {
            return false;
        }

        return true;
    };

    return (
        <WizardPage
            onNext={onNext}
            showPrevious={false}
            showCancel={false}
            nextLabel={translator(platformMessages.mlContinue)}
            skipWhen={skipOnResumeIf}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={submissionVM}
                overrideProps={overrides}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                classNameMap={styles}
                showErrors
            />
            <ScrollToError counter={errorTimestamp} />
        </WizardPage>
    );
};

AccountSyncPage.propTypes = wizardProps;
export default AccountSyncPage;
